//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// lunch-goal imports
import IGoalsWrapper from "./goals-wrapper.d";
import { BaseGoalComponent } from "./base-goal.component";
import * as styles from "./goals-wrapper.styles";
// other internals
import { logger } from "@util/logger.util";
import { timeSlots } from "@constants/time-slots.constant";
import { removeGoal, upsertLunch } from "@mutations";
import { standardGoals } from "@util/goal.util";
import { minutesInRange } from "@util/time-slot.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import ReduxState from "../../declarations/state.d";
import { H2 } from "@ui";
// material-ui
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
export class LunchGoalComponent extends BaseGoalComponent<IGoalsWrapper.ILunch.State> {
  private defaultGoalState: IGoalsWrapper.ILunch.State = {
    selected: true,
    loading: false,
    minLunchStart: (timeSlots.keys.T_12_00 as unknown) as ISchema.MinSlot,
    maxLunchEnd: (timeSlots.keys.T_12_30 as unknown) as ISchema.MaxSlot,
    minLunchDuration: 60,
    maxLunchDuration: 60,
  };

  constructor(props: IGoalsWrapper.Props) {
    super(props, standardGoals.Lunch);

    this.state = this.calculateStateFromProps();
    this.maybeSaveDefaults(); // must be called AFTER state set

    // binding class methods
    this.onToggle = this.onToggle.bind(this);
    this.onMinLunchSelected = this.onMinLunchSelected.bind(this);
    this.onMaxLunchSelected = this.onMaxLunchSelected.bind(this);
    this.onStartRangeSelected = this.onStartRangeSelected.bind(this);
    this.onEndRangeSelected = this.onEndRangeSelected.bind(this);
    this.onMutationError = this.onMutationError.bind(this);
  }

  private maybeSaveDefaults = () => {
    const goal = this.findGoal();
    const needsSave = (!goal || !goal.options) && this.props.saveDefaultOnMount;

    // careful! this will overwrite the user's settings with the defaults!
    if (needsSave) {
      this.updatePreferences(this.defaultGoalState, true);
    }
  };

  private calculateStateFromProps(): IGoalsWrapper.ILunch.State {
    const goal = this.findGoal();
    const goalOptions = goal && goal.options;
    const needsSave = (!goal || !goalOptions) && this.props.saveDefaultOnMount;
    const withinDayOptions =
      goalOptions && (goalOptions.specificOptions as ISchema.IGoalWithinDayOptions);

    if (needsSave) {
      return this.defaultGoalState;
    }

    return {
      selected: !!goal,
      loading: false,
      minLunchStart: (((withinDayOptions && withinDayOptions.minTimeSlot) ||
        timeSlots.keys.T_12_00) as unknown) as ISchema.MinSlot,
      maxLunchEnd: (((withinDayOptions && withinDayOptions.maxTimeSlot) ||
        timeSlots.keys.T_12_30) as unknown) as ISchema.MaxSlot,
      minLunchDuration: (withinDayOptions && withinDayOptions.minimumDuration) || 15,
      maxLunchDuration: (withinDayOptions && withinDayOptions.idealDuration) || 30,
    };
  }

  private anyErrors(): boolean {
    return (
      !!this.minDurationErrorText() || !!this.maxDurationErrorText() || !!this.lunchRangeErrorText()
    );
  }

  private createLunchOptions(
    newOptions: Partial<IGoalsWrapper.ILunch.State> = this.state,
  ): ISchema.IGoalPatchLunchOptionsInput {
    return {
      minTimeSlot: newOptions.minLunchStart as any,
      maxTimeSlot: newOptions.maxLunchEnd as any,
      idealMinutesDuration: newOptions.maxLunchDuration,
      minimumMinutesDuration: newOptions.minLunchDuration,
    };
  }

  // Handlers
  private onUpdatePreferencesSuccess = (noSnackbar?: boolean) => {
    if (!noSnackbar) {
      this.props.snackBarOpen("Updated preferences");
    }
  };

  private onUpdatePreferencesFailure = (noSnackbar?: boolean) => {
    if (!noSnackbar) {
      this.props.snackBarOpen("An error occurred!");
    }
  };

  private onMutateOnToggleSuccess = (isInputChecked: boolean) => {
    this.setState({ loading: false });
    this.props.snackBarOpen(`${isInputChecked ? "Added" : "Removed"} "Lunch" preference`);
  };

  private updatePreferences(
    newPreferences: Partial<IGoalsWrapper.ILunch.State>,
    noSnackbar?: boolean,
  ): void {
    const hasError = this.anyErrors();
    if (this.props.onChange) {
      this.props.onChange(hasError);
    }
    if (hasError) {
      return;
    }
    const options = this.createLunchOptions(newPreferences);
    const org = this.props.org;

    upsertLunch(
      this.props.relay.environment,
      { options, orgId: org.id },
      () => this.onUpdatePreferencesSuccess(noSnackbar),
      () => this.onUpdatePreferencesFailure(noSnackbar),
    );
  }

  protected mutateOnToggle(isInputChecked: boolean): void {
    const org = this.props.org;

    if (isInputChecked) {
      this.setState({ loading: true });
      upsertLunch(
        this.props.relay.environment,
        { orgId: org.id, options: this.createLunchOptions() },
        this.onMutateOnToggleSuccess.bind(this, isInputChecked),
        this.onMutationError,
      );
    } else {
      const goal = this.findGoalInput();
      if (goal) {
        this.setState({ loading: true });
        removeGoal(
          this.props.relay.environment,
          { goal, orgId: org.id },
          this.onMutateOnToggleSuccess.bind(this, isInputChecked),
          this.onMutationError,
        );
      } else {
        logger.debug("Couldn't find goal to remove");
      }
    }
  }

  private onMinLunchSelected(e: React.ChangeEvent<HTMLSelectElement>): void {
    const minLunchDuration = parseInt(e.target.value);
    const maxLunchDuration =
      minLunchDuration > this.state.maxLunchDuration
        ? minLunchDuration
        : this.state.maxLunchDuration;
    const newState = { minLunchDuration, maxLunchDuration };

    this.setState(newState, () => {
      this.updatePreferences(newState);
    });
  }

  private onMaxLunchSelected(e: React.ChangeEvent<HTMLSelectElement>): void {
    const maxLunchDuration = parseInt(e.target.value);
    const minLunchDuration =
      maxLunchDuration < this.state.minLunchDuration
        ? maxLunchDuration
        : this.state.minLunchDuration;
    const newState = { maxLunchDuration, minLunchDuration };

    this.setState(newState, () => {
      this.updatePreferences(newState);
    });
  }

  private onStartRangeSelected(e: React.ChangeEvent<HTMLSelectElement>): void {
    const minLunchStart = e.target.value as ISchema.MinSlot;
    this.setState({ minLunchStart }, () => {
      this.updatePreferences({ minLunchStart, maxLunchEnd: this.state.maxLunchEnd });
    });
  }

  private onEndRangeSelected(e: React.ChangeEvent<HTMLSelectElement>): void {
    const maxLunchEnd = e.target.value as ISchema.MaxSlot;
    this.setState({ maxLunchEnd }, () => {
      this.updatePreferences({ maxLunchEnd, minLunchStart: this.state.minLunchStart });
    });
  }

  private onMutationError(): void {
    this.setState({ loading: false });
    this.props.snackBarOpen("An error occurred!");
  }

  private renderDurationItems(durations: number[]): JSX.Element[] {
    return durations.map((duration) => (
      <MenuItem key={duration} value={duration}>
        {duration} minutes
      </MenuItem>
    ));
  }

  private minDurationErrorText(): string | null {
    const allowedRangeDuration = minutesInRange(this.state.minLunchStart, this.state.maxLunchEnd);

    return this.state.minLunchDuration > allowedRangeDuration
      ? "Minimum duration must be less than allowed time range"
      : null;
  }

  private maxDurationErrorText(): string | null {
    const allowedRangeDuration = minutesInRange(this.state.minLunchStart, this.state.maxLunchEnd);

    return this.state.maxLunchDuration > allowedRangeDuration
      ? "Ideal duration must be less than allowed time range"
      : null;
  }

  private renderDurations(): JSX.Element {
    const disabled = !this.state.selected || this.state.loading;

    const durationOptions = [15, 30, 45, 60, 90]; // TODO: constant-ize these

    const idealDurations = durationOptions; // durationOptions.filter(duration => duration >= this.state.minLunchDuration);
    const minDurations = durationOptions; // durationOptions.filter(duration => duration <= this.state.maxLunchDuration);

    return (
      <div style={styles.lunchContainer}>
        <FormControl style={styles.lunchForm} error={!!this.minDurationErrorText()}>
          <InputLabel shrink htmlFor="cw-minimum-lunch-duration-select">
            Minimum Lunch
          </InputLabel>
          <Select
            value={this.state.minLunchDuration}
            onChange={this.onMinLunchSelected}
            name="Minimum Lunch"
            disabled={disabled}
            inputProps={{
              name: "Minimum Lunch",
              id: "cw-minimum-lunch-duration-select",
            }}
          >
            {this.renderDurationItems(minDurations)}
          </Select>
          {this.minDurationErrorText() ? (
            <FormHelperText>{this.minDurationErrorText()}</FormHelperText>
          ) : (
            ""
          )}
        </FormControl>

        <FormControl style={styles.lunchForm} error={!!this.maxDurationErrorText()}>
          <InputLabel shrink htmlFor="cw-ideal-lunch-duration-select">
            Ideal Lunch
          </InputLabel>
          <Select
            value={this.state.maxLunchDuration}
            onChange={this.onMaxLunchSelected}
            name="Ideal Lunch"
            disabled={disabled}
            inputProps={{
              name: "Ideal Lunch",
              id: "cw-ideal-lunch-duration-select",
            }}
          >
            {this.renderDurationItems(idealDurations)}
          </Select>
          {this.maxDurationErrorText() ? (
            <FormHelperText>{this.maxDurationErrorText()}</FormHelperText>
          ) : (
            ""
          )}
        </FormControl>
      </div>
    );
  }

  private renderRangeItems(slots: string[], displayMap: { [slot: string]: string }): JSX.Element[] {
    return slots.map((slot) => (
      <MenuItem key={slot} value={slot}>
        {displayMap[slot]}
      </MenuItem>
    ));
  }

  private lunchRangeErrorText(): string | null {
    const allowedRangeDuration = minutesInRange(this.state.minLunchStart, this.state.maxLunchEnd);

    return this.state.maxLunchDuration > allowedRangeDuration
      ? "Allowed time range must be longer than ideal lunch duration"
      : null;
  }

  private renderLunchRange(): JSX.Element {
    const disabled = !this.state.selected || this.state.loading;

    const minRanges = Object.keys(timeSlots.min).filter((slot) => slot <= this.state.maxLunchEnd);
    const maxRanges = Object.keys(timeSlots.max).filter((slot) => slot >= this.state.minLunchStart);

    return (
      <div style={styles.lunchContainer}>
        <FormControl style={styles.lunchForm} error={!!this.lunchRangeErrorText()}>
          <InputLabel shrink htmlFor="cw-earliest-lunch-start-select">
            Earliest Lunch Start
          </InputLabel>
          <Select
            value={this.state.minLunchStart}
            onChange={this.onStartRangeSelected}
            name="Earliest Lunch Start"
            disabled={disabled}
            inputProps={{
              name: "Earliest Lunch Start",
              id: "cw-earliest-lunch-start-select",
            }}
          >
            {this.renderRangeItems(minRanges, timeSlots.min)}
          </Select>
          {this.lunchRangeErrorText() ? (
            <FormHelperText>{this.lunchRangeErrorText()}</FormHelperText>
          ) : (
            ""
          )}
        </FormControl>

        <FormControl style={styles.lunchForm} error={!!this.lunchRangeErrorText()}>
          <InputLabel shrink htmlFor="cw-latest-lunch-end-select">
            Latest Lunch End
          </InputLabel>
          <Select
            value={this.state.maxLunchEnd}
            onChange={this.onEndRangeSelected}
            name="Latest Lunch End"
            disabled={disabled}
            inputProps={{
              name: "Latest Lunch End",
              id: "cw-latest-lunch-end-select",
            }}
          >
            {this.renderRangeItems(maxRanges, timeSlots.max)}
          </Select>
          {this.lunchRangeErrorText() ? (
            <FormHelperText>{this.lunchRangeErrorText()}</FormHelperText>
          ) : (
            ""
          )}
        </FormControl>
      </div>
    );
  }

  private renderLunchOptions(): JSX.Element {
    return (
      <div style={!this.state.selected ? { cursor: "not-allowed" } : undefined}>
        <div style={this.state.selected ? styles.container : styles.containerDisabled}>
          {this.props.isWebSettings ? (
            <Typography variant="h3">When do you typically like to eat lunch?</Typography>
          ) : (
            <H2>When do you typically like to eat lunch?</H2>
          )}
          {this.renderDurations()}
          {this.renderLunchRange()}
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    const { classes } = this.props;

    return (
      <div style={styles.lunchGoal}>
        <FormControl>
          <FormControlLabel
            control={<Switch color="primary" checked={this.state.selected} />}
            onChange={this.onToggle}
            disabled={this.state.loading}
            label="Preserve time for lunch when possible"
            labelPlacement="end"
            classes={{ label: classes.toggleText }}
          />
        </FormControl>
        {this.props.isWebSettings ? <div style={{ marginBottom: 60 }} /> : ""}
        {this.renderLunchOptions()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const LunchGoalStyled = withStyles(styles.classes)(LunchGoalComponent);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: ReduxState.Redux, _ownProps: IGoalsWrapper.Container) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(LunchGoalStyled);

//////////////////
// RELAY
//////////////////
import { fragments } from "./LunchGoal.gql";
export const LunchGoal = createFragmentContainer(reduxContainer, fragments);
